import { Tutorial as TutorialName } from 'constants/tutorials';
import $store from 'stores';
import EventsManager from 'managers/events.manager';
import { Tutorial } from 'types';

const TUTORIALS_TRANSITION_DURATION = 0.6;

const getDroppables = () => $store.getters.droppablesActive;
const DATA: Tutorial[] = [
  {
    name: TutorialName.Intro,
    check: () => !$store.getters.hasProgress,
    next: TutorialName.Mix,
    hide: () => EventsManager.then('ELEMENT_DROP'),
  },
  {
    name: TutorialName.Mix,
    check: () => !$store.getters.hasProgress &&
      $store.getters.droppablesActive.length === 1,
    next: TutorialName.Guide,
    hide() {
      return new Promise((resolve) => {
        const handler = () => {
          if (getDroppables().length < 2) {
            return;
          }

          EventsManager.off('ELEMENT_DROP', handler);
          EventsManager.off('MIX_NEW', handler);

          resolve();
        };

        EventsManager.on('ELEMENT_DROP', handler);
        EventsManager.once('MIX_NEW', handler);
      });
    },
  },
  {
    name: TutorialName.Guide,
    check: () => !$store.getters.hasProgress &&
      getDroppables().length === 2,
    hide() {
      return new Promise((resolve) => {
        let handler: () => void;
        const off = () => {
          EventsManager.off('ELEMENT_DESTROY', handler);
          EventsManager.off('MIX_NEW', off);

          resolve();
        };

        handler = () => {
          if (getDroppables().length >= 1) {
            return;
          }

          off();
        };

        EventsManager.on('ELEMENT_DESTROY', handler);
        EventsManager.once('MIX_NEW', off);
      });
    },
  },
];

class TutorialsManager {
  public init() {
    EventsManager.once('PLAY', this.check);
    EventsManager.once('WORKSPACE_CLEAR', this.reset);
    EventsManager.once('LOGGED_IN', this.reset);

    EventsManager.onReady().then(this.notifyIfFinished);
  }
  public check = () => {
    if ($store.getters.isLoggedIn) {
      EventsManager.emit('TUTORIALS_FINISHED');
      return;
    }

    const tutorialToShow = DATA.find((tutorial) => tutorial.check()) || null;
    if (tutorialToShow) {
      this.show(tutorialToShow);
      return;
    }

    EventsManager.emit('TUTORIALS_FINISHED');
  }
  private notifyIfFinished = () => {
    const isLoggedIn = $store.getters.isLoggedIn;
    const willRun = DATA.some((tutorial) => tutorial.check());
    if (isLoggedIn || !willRun) {
      $store.commit('TUTORIALS_FINISHED');
    }
  }

  public show(tutorial: Tutorial) {
    $store.dispatch('tutorialShow', tutorial.name);
    EventsManager.emit('TUTORIALS_PROGRESS', { type: 'start', name: tutorial.name });

    tutorial.hide().then(() => {
      const next = this.getNext();
      this.hide();
      EventsManager.emit('TUTORIALS_PROGRESS', { type: 'end', name: tutorial.name });

      if (next) {
        this.showNext(next);
        return;
      }

      window.setTimeout(() => $store.dispatch('tutorialsFinished'), TUTORIALS_TRANSITION_DURATION * 1000);
    });
  }

  private getNext(current = this.current) {
    if (!current || !current.hasOwnProperty('next')) {
      return null;
    }

    const next = this.getByName(current.next);
    if (!next || !next.check()) {
      return null;
    }

    return next;
  }
  private showNext(next: Tutorial) {
    if (!next) return;

    window.setTimeout(() => {
      if (!next.check()) {
        const nextNext = this.getNext(next);
        if (!nextNext) {
          this.reset();
        } else {
          this.showNext(nextNext);
        }
        return;
      }

      this.show(next);
    }, TUTORIALS_TRANSITION_DURATION * 1000);
  }

  public hide() {
    $store.dispatch('tutorialHide');
  }
  public reset = () => {
    this.hide();
    $store.dispatch('tutorialsFinished');

    EventsManager.off('PLAY', this.check);
    EventsManager.off('WORKSPACE_CLEAR', this.reset);
    EventsManager.off('LOGGED_IN', this.reset);
  }

  get current() {
    const name = $store.getters.tutorial;
    if (!name) {
      return null;
    }

    return this.getByName(name);
  }
  private getByName(name?: TutorialName) {
    return DATA.find((tutorial) => tutorial.name === name);
  }
}

export default new TutorialsManager();
